import { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { NULL_VALUE } from 'constants/displayConstants';
import type { ResourceRequest } from 'components/IAP/hooks/incidentResourceRequestHooks';
import { use213rrStatusLabel } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrVersioning';
import { Ics213RrExpandedPanel } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrExpandedPanel';
import { ICS204_SURFACE } from 'components/IAP/planningView/widgets/AssignmentList/assignmentCardStyles';

export type Ics213RrListRowData = ResourceRequest & {
  /** Display label for status filter/search (from stored status). */
  statusLabel: string;
  resourcesSummary: string;
  workspaceName?: string;
};

type Ics213RrListRowProps = {
  item: Ics213RrListRowData;
  summaryColumns: string[];
  columnCount: number;
  expanded: boolean;
  onToggle: () => void;
  memberPositions: { positionId: string; positionName: string }[];
  reqCount: Record<string, number>;
  readOnly?: boolean;
  /** Scroll into view once when opened via deeplink / create. */
  scrollOnExpand?: boolean;
};

function StatusCell({
  requestId,
  storedStatus,
}: {
  requestId: string;
  storedStatus: string;
}) {
  const label = use213rrStatusLabel(requestId, storedStatus);
  return <span className="tw:truncate">{label}</span>;
}

function cellValue(item: Ics213RrListRowData, column: string): string {
  switch (column) {
    case 'requestNumber':
      return item.request_number || NULL_VALUE;
    case 'submittedBy':
      return item.requestor_name || NULL_VALUE;
    case 'status':
      return item.statusLabel || NULL_VALUE;
    case 'resources':
      return item.resourcesSummary || NULL_VALUE;
    default:
      return NULL_VALUE;
  }
}

/**
 * One 213RR as a table tbody: summary row + optional expanded detail
 * (mirrors AssignmentContainer).
 */
export function Ics213RrListRow({
  item,
  summaryColumns,
  columnCount,
  expanded,
  onToggle,
  memberPositions,
  reqCount,
  readOnly = false,
  scrollOnExpand = false,
}: Ics213RrListRowProps) {
  const headerRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!expanded || !scrollOnExpand) return;
    headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [expanded, scrollOnExpand]);

  return (
    <tbody data-testid="ics213rr-row">
      <TableRow
        ref={headerRef}
        data-testid="ics213rr-row-header"
        className="tw:cursor-pointer tw:bg-black tw:hover:bg-black"
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
      >
        <TableCell className={cn('tw:w-8', ICS204_SURFACE)}>
          <ChevronDown
            className={cn(
              'tw:h-4 tw:w-4 tw:transition-transform',
              expanded && 'tw:rotate-180'
            )}
          />
        </TableCell>
        {summaryColumns.map((column) => (
          <TableCell
            key={column}
            className={cn('tw:min-w-0 tw:text-left', ICS204_SURFACE)}
          >
            {column === 'status' ? (
              <StatusCell
                requestId={item.resource_request_id}
                storedStatus={item.status}
              />
            ) : column === 'requestNumber' ? (
              <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-2">
                <span className="tw:truncate">{cellValue(item, column)}</span>
                {item.workspaceName ? (
                  <Badge variant="outline">{item.workspaceName}</Badge>
                ) : null}
              </div>
            ) : (
              <span className="tw:truncate">{cellValue(item, column)}</span>
            )}
          </TableCell>
        ))}
      </TableRow>
      {expanded ? (
        <TableRow className="tw:hover:bg-transparent">
          <TableCell
            colSpan={columnCount}
            className="tw:h-auto tw:min-w-0 tw:whitespace-normal tw:p-0 tw:align-top"
          >
            <Ics213RrExpandedPanel
              request={item}
              memberPositions={memberPositions}
              reqCount={reqCount}
              readOnly={readOnly}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </tbody>
  );
}
